/**
 * useLocationSearch - Hook for location search with geocoding
 *
 * Features:
 * - Search by location name (Google Places API for POI/business)
 * - Search by address (Mapbox Geocoding API)
 * - Reverse geocoding for postcode when missing
 * - Edit mode support for pre-filling existing locations
 */

import React, { useState, useCallback } from 'react';
import {
  geocodeWithMapbox,
  GeocodedResult,
  reverseGeocodeForPostcode
} from '@/features/routing/utils/mapboxGeocoding';
import { searchPOIWithGoogle } from '@/features/routing/utils/googlePlaces';

export interface UseLocationSearchReturn {
  locationName: string;
  address: string;
  results: GeocodedResult[];
  selected: GeocodedResult | null;
  isSearching: boolean;
  error: string | null;
  lastSearchedField: 'name' | 'address' | null;
  /** Which search produced `selected`: a business listing ('name') or a street address. */
  selectedFrom: 'name' | 'address' | null;
  searchByName: () => Promise<void>;
  searchByAddress: (options?: { addressOnly?: boolean }) => Promise<void>;
  selectResult: (result: GeocodedResult) => Promise<void>;
  clearSelection: () => void;
  updateLocationName: (value: string) => void;
  updateAddress: (value: string) => void;
  reset: () => void;
  setLocationName: React.Dispatch<React.SetStateAction<string>>;
  setAddress: React.Dispatch<React.SetStateAction<string>>;
  setEditModeLocation: (location: {
    lat: number;
    lng: number;
    displayName: string;
    addressComponents: { city?: string; state?: string; postcode?: string };
  }) => void;
}

const formatAddress = (displayName: string): string => {
  return displayName
    .split(',')
    .slice(0, 3)
    .map(p => p.trim())
    .join(', ');
};

export function useLocationSearch(): UseLocationSearchReturn {
  const [locationName, setLocationName] = useState('');
  const [address, setAddress] = useState('');
  const [results, setResults] = useState<GeocodedResult[]>([]);
  const [selected, setSelected] = useState<GeocodedResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSearchedField, setLastSearchedField] = useState<'name' | 'address' | null>(null);
  const [selectedFrom, setSelectedFrom] = useState<'name' | 'address' | null>(null);

  // Search by location name (uses Google Places API for POI/business search)
  const searchByName = useCallback(async () => {
    const trimmed = locationName.trim();
    if (trimmed.length < 3) {
      setError('Please enter at least 3 characters to search');
      return;
    }

    setIsSearching(true);
    setError(null);
    setResults([]);
    setSelected(null);
    setSelectedFrom(null);
    setLastSearchedField('name');

    try {
      const geocoded = await searchPOIWithGoogle(trimmed, { limit: 5 });
      if (geocoded.length > 0) {
        setResults(geocoded);
      } else {
        setError('No results found. Try a different search term.');
      }
    } catch {
      setError('Search failed. Please try again.');
    }

    setIsSearching(false);
  }, [locationName]);

  // Search by address. `addressOnly` restricts results to street addresses
  // (customer homes), excluding place/street-level matches.
  const searchByAddress = useCallback(
    async (options?: { addressOnly?: boolean }) => {
      const trimmed = address.trim();
      if (trimmed.length < 5) {
        setError('Please enter a more complete address to search');
        return;
      }

      setIsSearching(true);
      setError(null);
      setResults([]);
      setSelected(null);
      setSelectedFrom(null);
      setLastSearchedField('address');

      try {
        const geocoded = await geocodeWithMapbox(trimmed, {
          limit: 5,
          types: options?.addressOnly ? ['address'] : ['address', 'place', 'street']
        });
        if (geocoded.length > 0) {
          setResults(geocoded);
        } else {
          setError('No results found. Try a different search term.');
        }
      } catch {
        setError('Search failed. Please try again.');
      }

      setIsSearching(false);
    },
    [address]
  );

  // Select a result - conditionally updates fields based on which search was used
  const selectResult = useCallback(
    async (result: GeocodedResult) => {
      setResults([]);
      setError(null);

      // Always update address
      setAddress(formatAddress(result.displayName));

      // Only update name if searched by name (not by address)
      if (lastSearchedField === 'name') {
        const firstPart = result.displayName.split(',')[0];
        const name = result.businessName || (firstPart ? firstPart.trim() : result.displayName);
        setLocationName(name);
      }

      setLastSearchedField(null);

      // If the result doesn't have a postcode, try reverse geocoding to get it
      let finalResult = result;
      const existingPostcode = result.addressComponents.postcode?.trim();

      if (!existingPostcode && result.lat && result.lng) {
        const reverseData = await reverseGeocodeForPostcode(result.lat, result.lng);
        if (reverseData?.postcode) {
          finalResult = {
            ...result,
            addressComponents: {
              ...result.addressComponents,
              postcode: reverseData.postcode
            }
          };
        }
      }

      setSelectedFrom(lastSearchedField);
      setSelected(finalResult);
    },
    [lastSearchedField]
  );

  const clearSelection = useCallback(() => {
    setSelected(null);
    setSelectedFrom(null);
    setLocationName('');
    setAddress('');
    setError(null);
    setLastSearchedField(null);
  }, []);

  const updateLocationName = useCallback(
    (value: string) => {
      setLocationName(value);
      setError(null);
      setResults([]);
      if (selected) {
        setSelected(null);
        setSelectedFrom(null);
        setAddress('');
      }
    },
    [selected]
  );

  const updateAddress = useCallback(
    (value: string) => {
      setAddress(value);
      setError(null);
      setResults([]);
      if (selected) {
        setSelected(null);
        setSelectedFrom(null);
        setLocationName('');
      }
    },
    [selected]
  );

  const reset = useCallback(() => {
    setLocationName('');
    setAddress('');
    setResults([]);
    setSelected(null);
    setSelectedFrom(null);
    setError(null);
    setLastSearchedField(null);
  }, []);

  // For edit mode: set location from existing data without geocoding
  const setEditModeLocation = useCallback(
    (location: {
      lat: number;
      lng: number;
      displayName: string;
      addressComponents: { city?: string; state?: string; postcode?: string };
    }) => {
      setSelectedFrom(null);
      setSelected({
        lat: location.lat,
        lng: location.lng,
        displayName: location.displayName,
        type: 'street_address' as const,
        importance: 1.0,
        addressComponents: {
          city: location.addressComponents.city || '',
          state: location.addressComponents.state || '',
          ...(location.addressComponents.postcode && {
            postcode: location.addressComponents.postcode
          })
        }
      });
    },
    []
  );

  return {
    locationName,
    address,
    results,
    selected,
    isSearching,
    error,
    lastSearchedField,
    selectedFrom,
    searchByName,
    searchByAddress,
    selectResult,
    clearSelection,
    updateLocationName,
    updateAddress,
    reset,
    setLocationName,
    setAddress,
    setEditModeLocation
  };
}

export type { GeocodedResult };
